class CartPromoNudge extends HTMLElement {
  constructor() {
    super();
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
  }

  connectedCallback() {
    this.handles = (this.dataset.handles || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
    this.excludeScope = this.dataset.scope === 'exclude';
    this.requiredQty = parseInt(this.dataset.requiredQty, 10) || 1;
    this.nudgeText = this.dataset.nudgeText || '';
    this.successText = this.dataset.successText || '';
    this.messageEl = this.querySelector('[data-message]');
    this.currentEl = this.querySelector('[data-progress-current]');
    this.progressBarEl = this.querySelector('[data-progress-bar]');

    this.eligibility = new Map();
    try {
      const initial = JSON.parse(this.dataset.eligibility || '{}');
      for (const [productId, eligible] of Object.entries(initial)) {
        this.eligibility.set(String(productId), Boolean(eligible));
      }
    } catch (_error) {
      // Malformed JSON: cache starts empty and is filled from the collection feed.
    }
    this.collectionProductIds = null;

    document.addEventListener('cart:updated', this.handleCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.handleCartUpdated);
  }

  async handleCartUpdated(event) {
    const cart = event?.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) return;
    const sequence = (this.sequence = (this.sequence || 0) + 1);

    const unknown = cart.items
      .map((item) => String(item.product_id))
      .filter((id) => id !== 'undefined' && !this.eligibility.has(id));
    if (unknown.length > 0) {
      const ids = await this.loadCollectionProductIds();
      if (ids) {
        for (const id of unknown) {
          const inCollection = ids.has(id);
          this.eligibility.set(id, this.excludeScope ? !inCollection : inCollection);
        }
      }
    }
    // A later cart update finished while this one was waiting on the feed.
    if (sequence !== this.sequence) return;

    let eligibleQty = 0;
    for (const item of cart.items) {
      if (this.eligibility.get(String(item.product_id)) === true) {
        eligibleQty += item.quantity || 0;
      }
    }
    this.render(eligibleQty);
  }

  // Product ids for every chosen collection, fetched once from the public
  // collection feed so cart updates need no backend. Resolves null when any
  // request fails so the next cart update retries instead of guessing.
  loadCollectionProductIds() {
    if (this.collectionProductIds) return Promise.resolve(this.collectionProductIds);
    if (this.collectionProductIdsPromise) return this.collectionProductIdsPromise;

    this.collectionProductIdsPromise = Promise.all(this.handles.map((handle) => this.fetchCollectionProductIds(handle)))
      .then((results) => {
        if (results.some((r) => r === null)) return null;
        this.collectionProductIds = new Set(results.flat());
        return this.collectionProductIds;
      })
      .catch(() => null)
      .finally(() => {
        this.collectionProductIdsPromise = null;
      });
    return this.collectionProductIdsPromise;
  }

  async fetchCollectionProductIds(handle) {
    const ids = [];
    for (let page = 1; page <= 20; page += 1) {
      const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      products.forEach((p) => ids.push(String(p.id)));
      if (products.length < 250) break;
    }
    return ids;
  }

  render(eligibleQty) {
    const unlocked = eligibleQty >= this.requiredQty;
    let message = '';
    if (unlocked) {
      message = this.successText;
    } else if (eligibleQty > 0) {
      message = this.nudgeText.replace('[remaining]', String(this.requiredQty - eligibleQty));
    }
    const cappedQty = Math.min(eligibleQty, this.requiredQty);
    this.classList.toggle('cart-promo-nudge--success', unlocked);
    this.classList.toggle('hide', message === '');
    if (this.messageEl) this.messageEl.textContent = message;
    if (this.currentEl) this.currentEl.textContent = String(cappedQty);
    if (this.progressBarEl) {
      this.progressBarEl.style.width = `${Math.round((cappedQty / this.requiredQty) * 100)}%`;
    }
  }
}

if (!customElements.get('cart-promo-nudge')) {
  customElements.define('cart-promo-nudge', CartPromoNudge);
}
